import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Heading,
  Link,
  useToast,
  HStack
} from '@chakra-ui/react';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

export const VerifyEmail = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const email = location.state?.email || '';

  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');

    if (!code) {
      setError('Please enter the verification code');
      return;
    }

    if (!email) {
      setError('Email not found. Please go back to signup.');
      return;
    }

    setLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      toast({
        title: 'Email verified successfully!',
        description: 'Your account is now active. You can log in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to login page
      navigate('/login', { 
        state: { 
          message: 'Account verified! Please log in with your credentials.',
          email: email
        }
      });

    } catch (error) {
      console.error('Verification error:', error);
      
      let errorMessage = 'Invalid verification code';
      
      if (error.message.includes('expired')) {
        errorMessage = 'Verification code has expired. Please request a new one.';
      } else if (error.message.includes('attempts')) {
        errorMessage = 'Too many failed attempts. Please request a new code.';
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Email not found. Please go back to signup.');
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      await resendSignUpCode({
        username: email
      });

      toast({
        title: 'Verification code sent!',
        description: 'Please check your email for a new verification code.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Resend error:', error);
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) {
    return (
      <Container maxW="md" py={12}>
        <VStack spacing={8}>
          <Heading size="lg" textAlign="center">
            Email Verification
          </Heading>
          
          <Alert status="error">
            <AlertIcon />
            No email found. Please go back to the signup page.
          </Alert>

          <Button onClick={() => navigate('/signup')}>
            Back to Signup
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <VStack spacing={8}>
        <Heading size="lg" textAlign="center">
          Verify Your Email
        </Heading>
        
        <Text textAlign="center" color="gray.600">
          We've sent a verification code to <strong>{email}</strong>. 
          Please enter the code below to complete your account setup.
        </Text>

        <Box w="100%" as="form" onSubmit={handleVerification}>
          <VStack spacing={4}>
            {error && (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>Verification Code</FormLabel>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                textAlign="center"
                fontSize="lg"
                letterSpacing="wider"
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              w="100%"
              isLoading={loading}
              loadingText="Verifying..."
            >
              Verify Email
            </Button>
          </VStack>
        </Box>

        <VStack spacing={2}>
          <Text fontSize="sm" color="gray.600">
            Didn't receive the code?
          </Text>
          <HStack spacing={4}>
            <Button
              variant="link"
              size="sm"
              onClick={handleResendCode}
              isLoading={resendLoading}
              loadingText="Sending..."
            >
              Resend Code
            </Button>
            <Text fontSize="sm" color="gray.400">|</Text>
            <Link
              fontSize="sm"
              color="blue.500"
              onClick={() => navigate('/signup')}
            >
              Back to Signup
            </Link>
          </HStack>
        </VStack>

        <Box textAlign="center" fontSize="sm" color="gray.500">
          <Text>
            Check your spam folder if you don't see the email in your inbox.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};